import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

// Asymmetric layout ratios — used via the `layout` prop. Stacked (grid-cols-1) below
// `sm`, then the ratio applies at `sm` and up — mirrors the `cols` variant below.
// When `layout` is set, `cols` is ignored.
//
// Each class is spelled out literally rather than assembled at runtime (e.g. from a
// separate ratio -> string map): Tailwind's build-time scanner only picks up classes
// it can find verbatim in source, so an interpolated arbitrary-value class would
// silently apply no styles at all.
// To add a new ratio: add an entry, e.g. "5-2": "sm:grid-cols-[5fr_2fr]".
// Usage: <Grid layout="3-2"> — left column gets 3 parts, right gets 2
const layoutResponsiveClasses = {
    "2-1": "sm:grid-cols-[2fr_1fr]", // wide left, narrow right — e.g. content + sidebar
    "1-2": "sm:grid-cols-[1fr_2fr]", // narrow left, wide right
    "3-1": "sm:grid-cols-[3fr_1fr]", // dominant left — e.g. main + thin aside
    "1-3": "sm:grid-cols-[1fr_3fr]", // dominant right
    "3-2": "sm:grid-cols-[3fr_2fr]", // slightly wider left — e.g. form + settings panel
    "2-3": "sm:grid-cols-[2fr_3fr]", // slightly wider right
    "4-3": "sm:grid-cols-[4fr_3fr]", // subtly wider left
} as const;

// Equal-column grids — used via the `cols` prop.
// Columns are responsive by default (stack on mobile, expand at sm/lg).
// To add more columns: add a new entry to the cols variant below.
// Usage: <Grid cols={3}> — three equal columns, responsive
const gridVariants = cva("grid items-start", {
    variants: {
        cols: {
            1: "grid-cols-1",
            2: "grid-cols-1 sm:grid-cols-2",
            3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
            4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
            5: "grid-cols-1 sm:grid-cols-3 lg:grid-cols-5",
            6: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6",
        },
        gap: {
            none: "gap-0",
            xs: "gap-1",
            sm: "gap-2",
            md: "gap-4",
            lg: "gap-6",
            xl: "gap-8",
            "2xl": "gap-12",
        },
    },
    defaultVariants: {
        cols: 2,
        gap: "md",
    },
});

export type GridLayout = keyof typeof layoutResponsiveClasses;

export interface GridProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof gridVariants> {
    layout?: GridLayout;
}

const Grid = React.forwardRef<HTMLDivElement, GridProps>(({ className, cols, layout, gap, ...props }, ref) => (
    <div
        ref={ref}
        className={cn(
            gridVariants({ cols: layout ? 1 : cols, gap }),
            layout && layoutResponsiveClasses[layout],
            className,
        )}
        {...props}
    />
));
Grid.displayName = "Grid";

export { Grid, gridVariants };
